/**
 * ConfidenceBadge - Qualitative trust indicator for non-journal sources.
 *
 * Renders a simple coloured pill ("High confidence", "Moderate confidence",
 * "Low confidence") derived from the authoritativeness_score. Used for
 * sources that lack a journal impact factor — visually distinct from the
 * CiteScoreBadge numeric box layout.
 *
 * Figma: node 7556:8122
 */

import { Badge } from '@heidi/design-system/badge';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { getConfidenceTier } from './authorityTiers';

interface ConfidenceBadgeProps {
  /** Authoritativeness score (0–100) used to determine confidence level */
  score: number | null | undefined;
  className?: string;
}

export function ConfidenceBadge({ score, className }: ConfidenceBadgeProps) {
  const { t } = useTranslations();
  const tier = getConfidenceTier(score);

  if (!tier) return null;

  return (
    <Badge
      color="neutral"
      size="sm"
      data-testid="source-reliability-classification"
      className={cn(tier.bgClass, tier.textClass, className)}
    >
      {t(tier.labelKey)}
    </Badge>
  );
}
